import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth as useClerkAuth } from '@clerk/react';

import { isOnboardingGateDisabled } from '@/api/onboarding/onboarding';
import { readPendingCompanyName, readPendingTenantId } from '@/api/tenants/linkClerkOrg';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { isClerkAuthFlowPath } from '@/utils/auth/clerkAuthPaths';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';
import { AppRoutes } from '@/utils/routes/router';

const LOG = '[ChurnSignal][PathB-Org]';

function hasPathBPendingRegistration(): boolean {
  return Boolean(readPendingTenantId() || readPendingCompanyName());
}

function CognitoPublicRoute({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    navigate(isOnboardingGateDisabled ? AppRoutes.dashboard : AppRoutes.onboardingForm, { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <div className="checkingAuth"></div>;
  }

  if (isAuthenticated) {
    return <div className="checkingAuth"></div>;
  }

  return <>{children}</>;
}

function ClerkPublicRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isLoading } = useAuth();
  const clerkAuth = useClerkAuth();

  const clerkSignedIn = clerkAuth.isLoaded && clerkAuth.isSignedIn;
  const onAuthEntry = isClerkAuthFlowPath(location.pathname);
  const pathBOrgConfirmPending =
    clerkSignedIn && onAuthEntry && isPathBCustomOrgConfirmEnabled() && hasPathBPendingRegistration();
  // Never yank users off /sign-in or /sign-up (bare or nested) while Clerk finishes OTP / tasks.
  const clerkFlowInProgress = onAuthEntry || pathBOrgConfirmPending;
  // Signed-in on `/` etc.: hold UI — ClerkSignedInRedirect chooses choose-org vs dashboard.
  // Do not navigate to dashboard from here (HAR: that raced / canceled choose-organization).
  const holdForSignedInRedirect = clerkSignedIn && !clerkFlowInProgress;
  const waitingForAuth = !clerkAuth.isLoaded;

  useEffect(() => {
    if (!onAuthEntry && !holdForSignedInRedirect) {
      return;
    }
    console.info(LOG, 'PublicRoute', {
      pathname: location.pathname,
      clerkSignedIn,
      onAuthEntry,
      clerkFlowInProgress,
      holdForSignedInRedirect,
      pendingTenantId: readPendingTenantId(),
    });
  }, [onAuthEntry, location.pathname, clerkSignedIn, clerkFlowInProgress, holdForSignedInRedirect]);

  if ((waitingForAuth || isLoading) && !clerkFlowInProgress) {
    return <div className="checkingAuth"></div>;
  }

  if (holdForSignedInRedirect) {
    return <div className="checkingAuth"></div>;
  }

  return <>{children}</>;
}

const PublicRoute = ({ children }: { children: ReactNode }) => {
  if (isClerkAuthEnabled()) {
    return <ClerkPublicRoute>{children}</ClerkPublicRoute>;
  }

  return <CognitoPublicRoute>{children}</CognitoPublicRoute>;
};

export default PublicRoute;
